/**
 * Application-level settings modal.
 *
 * Accessible from the project menu (top-left hamburger) or via Ctrl+, shortcut.
 * Features tabbed sections, in-modal search that highlights/filters settings,
 * and persisted state via the Zustand store.
 */

import {
  Cpu,
  Eye,
  Keyboard,
  Navigation,
  Settings,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getShortcutLabel } from "../app/keymap.ts";
import { MEMORY_LIMIT_OPTIONS } from "../app/memoryGovernor.ts";
import { useStore } from "../app/store.ts";
import { UNDO_BUDGET_OPTIONS } from "../app/storeUndo.ts";
import { hasTouchScreen } from "../viewport/interactionConstants.ts";
import { IconButton } from "./Button.tsx";
import {
  Kbd,
  LabeledRow,
  SearchField,
  SectionLabel,
  SegmentedControl,
  SliderRow,
  TabBar,
  Toggle,
  useModalKeyboard,
  useOutsideClick,
} from "./shared.tsx";
import { GLASS_MODAL, MODAL_BACKDROP_STYLE } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_HIGHLIGHT_BG,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_CAPTION,
  FONT_SIZE_MODAL_TITLE,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
  RADIUS_SM,
  Z_MODAL,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Settings definition — declarative structure for all settings
// ---------------------------------------------------------------------------

interface SettingToggle {
  kind: "toggle";
  id: string;
  label: string;
  description?: string;
  /** Store selector returning current value */
  get: (s: ReturnType<typeof useStore.getState>) => boolean;
  /** Store action name */
  set: (v: boolean) => void;
}

interface SettingSlider {
  kind: "slider";
  id: string;
  label: string;
  description?: string;
  get: (s: ReturnType<typeof useStore.getState>) => number;
  set: (v: number) => void;
  min: number;
  max: number;
  /** Multiplier between internal value and display (e.g. 100 for percentage) */
  displayMul?: number;
  displaySuffix?: string;
  decimals?: number;
}

interface SettingSegmented {
  kind: "segmented";
  id: string;
  label: string;
  description?: string;
  options: { label: string; value: string }[];
  get: (s: ReturnType<typeof useStore.getState>) => string;
  set: (v: string) => void;
}

interface SettingCustom {
  kind: "custom";
  id: string;
  label: string;
  description?: string;
  /** A React component (capital-letter function) rendered inline. */
  Component: () => React.ReactNode;
}

type SettingDef =
  | SettingToggle
  | SettingSlider
  | SettingSegmented
  | SettingCustom;

interface SettingsSection {
  id: string;
  label: string;
  settings: SettingDef[];
}

interface SettingsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
  sections: SettingsSection[];
}

// ---------------------------------------------------------------------------
// Custom setting components (must be true components to use hooks)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

function useSettingsTabs(): SettingsTab[] {
  const store = useStore;

  return useMemo(
    () => [
      {
        id: "interface",
        label: "Interface",
        icon: <SlidersHorizontal size={14} />,
        sections: [
          {
            id: "layout",
            label: "Layout",
            settings: [
              {
                kind: "segmented",
                id: "uiScale",
                label: "UI Scale",
                description:
                  "Scale all interface elements. Smaller fits more on screen, larger improves readability.",
                options: [
                  { label: "Small", value: "0.85" },
                  { label: "Default", value: "1" },
                  { label: "Large", value: "1.15" },
                  { label: "XL", value: "1.3" },
                ],
                get: (s) => String(s.uiScale),
                set: (v: string) => store.getState().setUiScale(Number(v)),
              },
            ],
          },
        ],
      },
      {
        id: "viewport",
        label: "Viewport",
        icon: <Eye size={14} />,
        sections: [
          {
            id: "overlays",
            label: "Overlays",
            settings: [
              {
                kind: "toggle",
                id: "showGrid",
                label: "Ground Grid",
                description: "Show the ground-plane reference grid",
                get: (s) => s.showGrid,
                set: (v: boolean) => store.getState().setShowGrid(v),
              },
              {
                kind: "toggle",
                id: "showAxisIndicator",
                label: "Axis Indicator",
                description: "Show the orientation axis arrows in the corner",
                get: (s) => s.showAxisIndicator,
                set: (v: boolean) => store.getState().setShowAxisIndicator(v),
              },
              {
                kind: "toggle",
                id: "showFrustum",
                label: "Camera Frustum",
                description: "Show the camera frustum frame overlay",
                get: (s) => s.showFrustum,
                set: (v: boolean) => store.getState().setShowFrustum(v),
              },
            ],
          },
          {
            id: "textures",
            label: "Textures",
            settings: [
              {
                kind: "toggle",
                id: "showTextures",
                label: "Show Textures",
                description:
                  "Display UV-mapped base color textures in solid and matcap modes",
                get: (s) => s.showTextures,
                set: (v: boolean) => store.getState().setShowTextures(v),
              },
              {
                kind: "toggle",
                id: "textureMapsEnabled",
                label: "Material Maps",
                description:
                  "Enable UV material map stack (normal, roughness, metallic, emission). Disabling uses flat material values instead.",
                get: (s) => s.textureMapsEnabled,
                set: (v: boolean) => store.getState().setTextureMapsEnabled(v),
              },
            ],
          },
          {
            id: "quality",
            label: "Quality",
            settings: [
              {
                kind: "segmented",
                id: "viewportDpr",
                label: "Resolution",
                description:
                  "Device-pixel-ratio ceiling for the live canvas, shared across draft and render modes. Adaptive performance may reduce further on heavy scenes.",
                options: [
                  { label: "1×", value: "1" },
                  { label: "1.5×", value: "1.5" },
                  { label: "2×", value: "2" },
                ],
                get: (s) => String(s.viewportQuality.maxViewportDpr),
                set: (v: string) =>
                  store
                    .getState()
                    .setViewportQuality("maxViewportDpr", Number(v)),
              },
              {
                kind: "segmented",
                id: "viewportShadowMap",
                label: "Shadow Map",
                description:
                  "Shadow-map texture size for directional and point lights in the live canvas. Larger sizes cost more GPU memory per frame.",
                options: [
                  { label: "256", value: "256" },
                  { label: "512", value: "512" },
                  { label: "1024", value: "1024" },
                  { label: "2048", value: "2048" },
                ],
                get: (s) => String(s.viewportQuality.shadowMapResolution),
                set: (v: string) =>
                  store
                    .getState()
                    .setViewportQuality("shadowMapResolution", Number(v)),
              },
            ],
          },
          {
            id: "exportQuality",
            label: "Export Overrides",
            settings: [
              {
                kind: "segmented",
                id: "exportDpr",
                label: "Resolution",
                description:
                  "Override the live resolution for rendered exports. Inherit keeps the viewport value; higher values improve final quality without affecting interactive performance.",
                options: [
                  { label: "Inherit", value: "inherit" },
                  { label: "1×", value: "1" },
                  { label: "1.5×", value: "1.5" },
                  { label: "2×", value: "2" },
                ],
                get: (s) =>
                  s.exportQualityOverrides.maxViewportDpr == null
                    ? "inherit"
                    : String(s.exportQualityOverrides.maxViewportDpr),
                set: (v: string) =>
                  store
                    .getState()
                    .setExportQualityOverride(
                      "maxViewportDpr",
                      v === "inherit" ? null : Number(v),
                    ),
              },
              {
                kind: "segmented",
                id: "exportShadowMap",
                label: "Shadow Map",
                description:
                  "Override the live shadow-map size when generating a rendered export. Inherit uses the live viewport value.",
                options: [
                  { label: "Inherit", value: "inherit" },
                  { label: "256", value: "256" },
                  { label: "512", value: "512" },
                  { label: "1024", value: "1024" },
                  { label: "2048", value: "2048" },
                ],
                get: (s) =>
                  s.exportQualityOverrides.shadowMapResolution == null
                    ? "inherit"
                    : String(s.exportQualityOverrides.shadowMapResolution),
                set: (v: string) =>
                  store
                    .getState()
                    .setExportQualityOverride(
                      "shadowMapResolution",
                      v === "inherit" ? null : Number(v),
                    ),
              },
            ],
          },
        ],
      },
      {
        id: "navigation",
        label: "Navigation",
        icon: <Navigation size={14} />,
        sections: [
          {
            id: "sensitivity",
            label: "Sensitivity",
            settings: [
              {
                kind: "segmented",
                id: "orbitSensitivity",
                label: "Orbit",
                description: "Rotation speed when orbiting the camera.",
                options: [
                  { label: "Low", value: "0.5" },
                  { label: "Default", value: "1" },
                  { label: "High", value: "1.5" },
                  { label: "Ultra", value: "2.5" },
                ],
                get: (s) => String(s.orbitSensitivity),
                set: (v: string) =>
                  store.getState().setOrbitSensitivity(Number(v)),
              },
              {
                kind: "segmented",
                id: "panSensitivity",
                label: "Pan",
                description: "Speed when panning (translating) the camera.",
                options: [
                  { label: "Low", value: "0.5" },
                  { label: "Default", value: "1.5" },
                  { label: "High", value: "2.5" },
                  { label: "Ultra", value: "3.5" },
                ],
                get: (s) => String(s.panSensitivity),
                set: (v: string) =>
                  store.getState().setPanSensitivity(Number(v)),
              },
              {
                kind: "segmented",
                id: "dollySensitivity",
                label: "Zoom",
                description: "Speed when zooming (dollying) in and out.",
                options: [
                  { label: "Low", value: "0.5" },
                  { label: "Default", value: "1" },
                  { label: "High", value: "1.5" },
                  { label: "Ultra", value: "2.5" },
                ],
                get: (s) => String(s.dollySensitivity),
                set: (v: string) =>
                  store.getState().setDollySensitivity(Number(v)),
              },
            ],
          },
          {
            id: "invert",
            label: "Invert",
            settings: [
              {
                kind: "toggle",
                id: "invertOrbit",
                label: "Invert Orbit",
                description: "Reverse orbit (rotation) direction.",
                get: (s) => s.invertOrbit,
                set: (v: boolean) => store.getState().setInvertOrbit(v),
              },
              {
                kind: "toggle",
                id: "invertPan",
                label: "Invert Pan",
                description: "Reverse pan (translation) direction.",
                get: (s) => s.invertPan,
                set: (v: boolean) => store.getState().setInvertPan(v),
              },
              {
                kind: "toggle",
                id: "invertZoom",
                label: "Invert Zoom",
                description: "Reverse zoom (dolly) direction.",
                get: (s) => s.invertZoom,
                set: (v: boolean) => store.getState().setInvertZoom(v),
              },
            ],
          },
        ],
      },
      {
        id: "system",
        label: "System",
        icon: <Cpu size={14} />,
        sections: [
          {
            id: "performance",
            label: "Performance",
            settings: [
              {
                kind: "segmented",
                id: "memoryLimit",
                label: "Memory Limit",
                description:
                  "Global ceiling on app memory. Warns at 80%, clears caches and trims undo at 90%, blocks new geometry at 100%. Off disables all checks.",
                options: MEMORY_LIMIT_OPTIONS.map((option) => ({
                  label: option.label,
                  value: String(option.bytes),
                })),
                get: (s) => String(s.memoryLimitBytes),
                set: (v: string) => store.getState().setMemoryLimit(Number(v)),
              },
              {
                kind: "segmented",
                id: "undoMemoryBudget",
                label: "Undo History Budget",
                description:
                  "Cap on memory retained for undo snapshots. Larger scenes produce larger snapshots; stay conservative on low-memory devices.",
                options: UNDO_BUDGET_OPTIONS.map((option) => ({
                  label: option.label,
                  value: String(option.bytes),
                })),
                get: (s) => String(s.undoMemoryBudgetBytes),
                set: (v: string) =>
                  store.getState().setUndoMemoryBudget(Number(v)),
              },
            ],
          },
          {
            id: "debug",
            label: "Developer",
            settings: [
              {
                kind: "toggle",
                id: "debugEnabled",
                label: "Debug Overlay",
                description: "Show performance and debug information overlay",
                get: (s) => s.debugEnabled,
                set: (v: boolean) => store.getState().setDebugEnabled(v),
              },
            ],
          },
        ],
      },
      {
        id: "keymap",
        label: "Keymap",
        icon: <Keyboard size={14} />,
        sections: [
          {
            id: "keymapRef",
            label: "Keyboard Shortcuts",
            settings: [],
          },
        ],
      },
    ],
    [],
  );
}

// ---------------------------------------------------------------------------
// Shortcuts reference data
// ---------------------------------------------------------------------------

/** Helper: look up a shortcut key from the keymap, with fallback. */
function kb(action: string, fallback: string): string {
  return getShortcutLabel(action) ?? fallback;
}

const SHORTCUT_GROUPS = [
  {
    label: "Navigation",
    shortcuts: [
      { keys: "Middle Drag", action: "Orbit" },
      { keys: "Shift+Middle Drag", action: "Pan" },
      { keys: "Scroll", action: "Zoom" },
      { keys: "Numpad .", action: "Focus selection" },
      {
        keys: kb("toggleFlyMode", "Shift+`"),
        action: "Toggle fly mode (WASD)",
      },
    ],
  },
  {
    label: "Selection",
    shortcuts: [
      { keys: "Left Click", action: "Select object" },
      { keys: "Shift+Click", action: "Add to selection" },
      { keys: `${kb("box_select", "B")} + Drag`, action: "Box select" },
      { keys: `${kb("brush_select", "C")} + Drag`, action: "Brush select" },
      { keys: kb("selectAll", "A"), action: "Select all" },
    ],
  },
  {
    label: "Transform",
    shortcuts: [
      { keys: kb("translate", "G"), action: "Grab / Move" },
      { keys: kb("rotate", "R"), action: "Rotate" },
      { keys: kb("scale", "S"), action: "Scale" },
      { keys: kb("duplicate", "Shift+D"), action: "Duplicate" },
      { keys: "X / Y / Z", action: "Axis constraint" },
      { keys: kb("command.confirm", "Enter"), action: "Confirm" },
      { keys: kb("command.cancel", "Escape"), action: "Cancel" },
    ],
  },
  {
    label: "Edit Mode",
    shortcuts: [
      { keys: kb("toggleEditPoseMode", "Tab"), action: "Toggle edit mode" },
      { keys: kb("toggleAddObject", "Shift+A"), action: "Add object" },
      { keys: kb("extrude", "E"), action: "Extrude" },
      { keys: kb("inset_faces", "I"), action: "Inset" },
      { keys: kb("insert_edge_loop", "Ctrl+R"), action: "Loop cut" },
      { keys: "X / Delete", action: "Delete" },
      { keys: kb("fill", "F"), action: "Fill face" },
    ],
  },
  {
    label: "Viewport",
    shortcuts: [
      {
        keys: kb("cycleRenderMethod", "Z"),
        action: "Render method menu (pie)",
      },
      {
        keys: kb("openRenderMethodModal", "Ctrl+Shift+,"),
        action: "Render method settings",
      },
      { keys: kb("openSnapPie", "Shift+S"), action: "Snap menu (pie)" },
      { keys: kb("openViewPie", "`"), action: "Axis views (pie)" },
      { keys: kb("toggleObjectXray", "Alt+Z"), action: "Toggle object x-ray" },
    ],
  },
  {
    label: "File",
    shortcuts: [
      { keys: kb("newScene", "Ctrl+N"), action: "New scene" },
      { keys: kb("openScene", "Ctrl+O"), action: "Open scene" },
      { keys: kb("saveScene", "Ctrl+S"), action: "Save scene" },
      { keys: kb("undo", "Ctrl+Z"), action: "Undo" },
      { keys: kb("redo", "Ctrl+Shift+Z"), action: "Redo" },
      { keys: kb("toggleOperatorSearch", "F3"), action: "Operator search" },
      { keys: kb("toggleSettings", "Ctrl+,"), action: "Settings" },
    ],
  },
];

// ---------------------------------------------------------------------------
// SettingsModal component
// ---------------------------------------------------------------------------

export function SettingsModal() {
  const settingsOpen = useStore((s) => s.settingsOpen);
  const setSettingsOpen = useStore((s) => s.setSettingsOpen);

  if (!settingsOpen) return null;

  return <SettingsModalInner onClose={() => setSettingsOpen(false)} />;
}

function SettingsModalInner({ onClose }: { onClose: () => void }) {
  const tabs = useSettingsTabs();
  // Seed from the deep-link signal once on mount; subsequent navigation is
  // local. The store-side signal is cleared on close (see setSettingsOpen).
  const [activeTab, setActiveTab] = useState(
    () => useStore.getState().settingsInitialTab ?? "viewport",
  );
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Keyboard: Escape to close + block non-modifier keys from leaking
  useModalKeyboard(true, { onEscape: onClose });

  // Close on click outside
  useOutsideClick(modalRef, onClose);

  // Focus search on mount (skip on touch devices to avoid keyboard popup)
  useEffect(() => {
    if (!hasTouchScreen()) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, []);

  const searchLower = search.toLowerCase();
  const isSearching = searchLower.length > 0;

  // When searching, show all matching settings across all tabs
  const matchingTabs = useMemo(() => {
    if (!isSearching) return null;
    return tabs
      .map((tab) => ({
        ...tab,
        sections: tab.sections
          .map((section) => ({
            ...section,
            settings: section.settings.filter(
              (s) =>
                s.label.toLowerCase().includes(searchLower) ||
                (s.description?.toLowerCase().includes(searchLower) ?? false),
            ),
          }))
          .filter(
            (section) =>
              section.settings.length > 0 ||
              section.label.toLowerCase().includes(searchLower),
          ),
      }))
      .filter((tab) => tab.sections.length > 0);
  }, [tabs, isSearching, searchLower]);

  const currentTab = tabs.find((t) => t.id === activeTab) ?? tabs[0];

  return (
    <>
      {/* Backdrop */}
      <div style={MODAL_BACKDROP_STYLE} />
      {/* Modal */}
      <div
        ref={modalRef}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 620,
          maxWidth: "calc(100vw - 40px)",
          maxHeight: "calc(100vh - 80px)",
          display: "flex",
          flexDirection: "column",
          ...GLASS_MODAL,
          zIndex: Z_MODAL,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "16px 20px 12px",
            gap: 10,
            borderBottom: `0.5px solid ${COLOR_BORDER}`,
            flexShrink: 0,
          }}
        >
          <Settings
            size={16}
            style={{ color: COLOR_TEXT_DIM, flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: FONT_SIZE_MODAL_TITLE,
              fontWeight: 600,
              color: COLOR_TEXT,
              flex: 1,
            }}
          >
            Settings
          </span>
          <IconButton
            onClick={onClose}
            label="Close"
            icon={<X size={16} />}
            size={28}
            radius={RADIUS_CONTROL}
            style={{ color: COLOR_TEXT_DIM, flexShrink: 0 }}
          />
        </div>

        {/* Search bar */}
        <div
          style={{
            padding: "10px 20px",
            borderBottom: `0.5px solid ${COLOR_BORDER}`,
            flexShrink: 0,
          }}
        >
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search settings…"
            inputRef={searchRef}
            onKeyDown={(e) => {
              if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
            }}
            clearable
          />
        </div>

        {/* Tab bar (hidden during search) */}
        {!isSearching && (
          <div style={{ padding: "0 20px", flexShrink: 0 }}>
            <TabBar
              tabs={tabs}
              active={activeTab}
              onChange={setActiveTab}
              variant="underline"
            />
          </div>
        )}

        {/* Content area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 20px 20px",
          }}
        >
          {isSearching ? (
            <SearchResults tabs={matchingTabs!} searchTerm={searchLower} />
          ) : currentTab.id === "keymap" ? (
            <ShortcutsContent />
          ) : (
            <TabContent tab={currentTab} searchTerm="" />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab content renderer
// ---------------------------------------------------------------------------

function TabContent({
  tab,
  searchTerm,
}: {
  tab: SettingsTab;
  searchTerm: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {tab.sections.map((section) => (
        <SettingsSectionView
          key={section.id}
          section={section}
          searchTerm={searchTerm}
        />
      ))}
    </div>
  );
}

function SearchResults({
  tabs,
  searchTerm,
}: {
  tabs: SettingsTab[];
  searchTerm: string;
}) {
  if (tabs.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: COLOR_TEXT_DIM,
          fontSize: FONT_SIZE_BODY,
        }}
      >
        No settings match your search.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {tabs.map((tab) => (
        <div key={tab.id}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_ACCENT,
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            {tab.icon}
            {tab.label}
          </div>
          {tab.sections.map((section) => (
            <SettingsSectionView
              key={section.id}
              section={section}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function SettingsSectionView({
  section,
  searchTerm,
}: {
  section: SettingsSection;
  searchTerm: string;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <SectionLabel>{section.label}</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {section.settings.map((setting) => (
          <SettingRow
            key={setting.id}
            setting={setting}
            highlight={searchTerm}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual setting row
// ---------------------------------------------------------------------------

function SettingRow({
  setting,
  highlight,
}: {
  setting: SettingDef;
  highlight: string;
}) {
  if (setting.kind === "toggle") {
    return <ToggleSettingRow setting={setting} highlight={highlight} />;
  }
  if (setting.kind === "segmented") {
    return <SegmentedSettingRow setting={setting} highlight={highlight} />;
  }
  if (setting.kind === "custom") {
    return <CustomSettingRow setting={setting} highlight={highlight} />;
  }
  return <SliderSettingRow setting={setting} highlight={highlight} />;
}

function ToggleSettingRow({
  setting,
  highlight,
}: {
  setting: SettingToggle;
  highlight: string;
}) {
  const value = useStore(setting.get);
  return (
    <div
      style={{
        padding: "6px 10px",
        borderRadius: RADIUS_SM,
        background: highlight ? COLOR_HIGHLIGHT_BG : "transparent",
      }}
    >
      <Toggle label={setting.label} checked={value} onChange={setting.set} />
      {setting.description && (
        <div
          style={{
            fontSize: FONT_SIZE_CAPTION,
            color: COLOR_TEXT_FAINT,
            marginTop: 2,
            lineHeight: 1.4,
          }}
        >
          {setting.description}
        </div>
      )}
    </div>
  );
}

function SliderSettingRow({
  setting,
  highlight,
}: {
  setting: SettingSlider;
  highlight: string;
}) {
  const rawValue = useStore(setting.get);
  const mul = setting.displayMul ?? 1;
  const displayVal = rawValue * mul;
  const decimals = setting.decimals ?? 2;
  const suffix = setting.displaySuffix ?? "";

  return (
    <div
      style={{
        padding: "6px 10px",
        borderRadius: RADIUS_SM,
        background: highlight ? COLOR_HIGHLIGHT_BG : "transparent",
      }}
    >
      <SliderRow
        label={setting.label}
        value={Math.round(displayVal)}
        min={setting.min}
        max={setting.max}
        displayValue={`${displayVal.toFixed(decimals)}${suffix}`}
        onChange={setting.set}
      />
      {setting.description && (
        <div
          style={{
            fontSize: FONT_SIZE_CAPTION,
            color: COLOR_TEXT_FAINT,
            marginTop: 2,
            lineHeight: 1.4,
          }}
        >
          {setting.description}
        </div>
      )}
    </div>
  );
}

function SegmentedSettingRow({
  setting,
  highlight,
}: {
  setting: SettingSegmented;
  highlight: string;
}) {
  const value = useStore(setting.get);
  return (
    <div
      style={{
        padding: "6px 10px",
        borderRadius: RADIUS_SM,
        background: highlight ? COLOR_HIGHLIGHT_BG : "transparent",
      }}
    >
      <LabeledRow label={setting.label} labelWidth={110}>
        <SegmentedControl
          options={setting.options}
          value={value}
          onChange={setting.set}
        />
      </LabeledRow>
      {setting.description && (
        <div
          style={{
            fontSize: FONT_SIZE_CAPTION,
            color: COLOR_TEXT_FAINT,
            marginTop: 2,
            lineHeight: 1.4,
          }}
        >
          {setting.description}
        </div>
      )}
    </div>
  );
}

function CustomSettingRow({
  setting,
  highlight,
}: {
  setting: SettingCustom;
  highlight: string;
}) {
  return (
    <div
      style={{
        padding: "6px 10px",
        borderRadius: RADIUS_SM,
        background: highlight ? COLOR_HIGHLIGHT_BG : "transparent",
      }}
    >
      <div
        style={{ fontSize: FONT_SIZE_BODY, color: COLOR_TEXT, marginBottom: 4 }}
      >
        {setting.label}
      </div>
      {setting.description && (
        <div
          style={{
            fontSize: FONT_SIZE_CAPTION,
            color: COLOR_TEXT_FAINT,
            marginBottom: 6,
            lineHeight: 1.4,
          }}
        >
          {setting.description}
        </div>
      )}
      <setting.Component />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shortcuts reference content
// ---------------------------------------------------------------------------

function ShortcutsContent() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {SHORTCUT_GROUPS.map((group) => (
        <div key={group.label}>
          <SectionLabel>{group.label}</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {group.shortcuts.map((shortcut) => (
              <div
                key={shortcut.keys}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "5px 10px",
                  borderRadius: RADIUS_SM,
                }}
              >
                <span
                  style={{
                    fontSize: FONT_SIZE_BODY,
                    color: COLOR_TEXT_SECONDARY,
                  }}
                >
                  {shortcut.action}
                </span>
                <Kbd shortcut={shortcut.keys} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
